/* eslint-disable import/prefer-default-export */
import React from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { css } from '@emotion/react';
import humps from 'humps';

// redux
import { addNewVote, updatePollQuestion } from 'redux/actions/polls';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import { RootState } from 'redux/schemas';
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import {
  PollNormalized,
  PollVieweeSubmissionNormalized,
  PollOptionNormalized,
  PollQuestionType,
} from 'redux/schemas/models/poll';
import { getPollLectureComponent, getPollQuestion, getNormalizedPollVieweeSubmission } from 'redux/selectors/polls';
import { useAppDispatch } from 'redux/store';
import { getLecturePage } from 'redux/selectors/timeline';
import { getCurrentCourse } from 'redux/selectors/course';

import BaseLectureComponentContext from 'lecture_pages/directives/components/base-lecture-component/context';
import { useLectureComponentContext } from 'components/lecture-component-provider';
import PusherService from 'shared/services/pusher-service';
import { TextAlign } from 'shared/components/nv-tooltip';
import { config } from '@config/pendo.config.json';

/* @ngInject */
export function useUpdatePoll() {
  const dispatch = useAppDispatch();
  const { catalogId, lecturePageId, lectureComponentId, angularComponent } = useLectureComponentContext();
  const lectureComponent = useGetPollLectureComponent();

  return React.useCallback(
    (newPoll: Partial<PollNormalized>) => {
      dispatch(
        updateLectureComponent({
          catalogId,
          lecturePageId,
          componentData: {
            id: lectureComponentId,
            type: lectureComponent.type,
            index: lectureComponent.index,
            poll: { id: lectureComponent.poll.id, ...newPoll } as any, // For some reason, this thinks that poll should be a number
          },
        }),
      );
    },
    [dispatch, catalogId, lecturePageId, lectureComponentId, lectureComponent.type, lectureComponent.index, lectureComponent.poll.id],
  );
}

/**
 * The Poll Lecture Component wraps the component with a provider to pass down
 * data as the lectureComponentId to children. This custom hook optionally
 * receives the lectureComponentId as a parameter, otherwise grabs the value
 * from that context
 */
/* @ngInject */
export function useGetPollLectureComponent(passedLectureComponentId?: number) {
  const contextLectureComponentId = useLectureComponentContext().lectureComponentId;

  const lectureComponentId = String(passedLectureComponentId ?? contextLectureComponentId);
  return useSelector<RootState, LectureComponent<ComponentType.POLL>>((state) => getPollLectureComponent(state, { lectureComponentId }));
}

/* @ngInject */
export function usePollEditDropdown(lectureComponent: LectureComponent<ComponentType.POLL>) {
  const baseComponentContext = React.useContext(BaseLectureComponentContext);
  const { sharedProps, setSharedProps } = baseComponentContext;
  const { catalogId } = useLectureComponentContext();
  const updater = useUpdatePoll();
  const dispatch = useDispatch();

  const lecturePage = useSelector((state) => getLecturePage(state, lectureComponent.lecturePageId));
  const course = useSelector((state) => getCurrentCourse(state));
  const pollQuestion = useSelector((state) => getPollQuestion(state, { pollQuestionId: lectureComponent.poll.questionIds[0] }));

  const canMakeStructuralChanges = course.isContentManagementCollection
    ? !lectureComponent.poll.learnerStartedSubmission
    : (!lecturePage.released && !lecturePage.isLinked);

  React.useEffect(() => {
    setSharedProps({
      // TODO: Make this configurable
      // deleteConfirmationMessage: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.DELETE_WARNING(),
      ...sharedProps,
      extraOptions: {
        mode: 'prepend',
        options: [
          // self paced course cannot have deadline
          ...(course.isSelfPaced
            ? []
            : [
              {
                type: 'text',
                text: lectureComponent.poll.expirationDate
                  ? t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.REMOVE_DEADLINE()
                  : t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.ADD_DEADLINE(),
                disabled: course.isContentManagementCollection,
                tooltip: {
                  enabled: course.isContentManagementCollection,
                  text: t.LECTURE_PAGES.COMPONENTS.DROPDOWN.SETUP_IN_LINKED_LESSON_TOOLTIP(),
                  placement: 'left',
                  offset: 20,
                },
                callback: () => {
                  updater({
                    expirationDate: lectureComponent.poll.expirationDate
                      ? null
                      : moment(lecturePage.releaseDate).add(1, 'weeks').endOf('day').toISOString(),
                  });
                },
              } as const,
              {
                type: 'divider',
              } as const,
            ]),
          {
            type: 'radio',
            label: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.SOCIAL_POLL(),
            id: 'poll-response-privacy-settings-social',
            group: 'poll-response-privacy-settings',
            itemStyle: editDropdownRadioButtonStyle,
            infoIconTooltipText: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.SOCIAL_POLL_INFO_TOOLTIP(),
            isChecked: lectureComponent.poll.responsePrivacySetting === 'social',
            dataQa: config.pendo.polls.selectSocialPoll,
            callback: () => {
              updater({
                responsePrivacySetting: 'social',
              });
            },
            disabled: !!lecturePage.isLinked,
            tooltip: {
              text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
              textAlign: TextAlign.LEFT,
              enabled: !!lecturePage.isLinked,
              placement: 'left',
            },
          },
          {
            type: 'radio',
            label: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.ANONYMOUS_POLL(),
            id: 'poll-response-privacy-settings-anonymous',
            group: 'poll-response-privacy-settings',
            itemStyle: editDropdownRadioButtonStyle,
            infoIconTooltipText: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.ANONYMOUS_POLL_INFO_TOOLTIP(),
            isChecked: lectureComponent.poll.responsePrivacySetting === 'anonymous',
            dataQa: config.pendo.polls.selectAnonymousPoll,
            callback: () => {
              updater({
                responsePrivacySetting: 'anonymous',
              });
            },
            disabled: !!lecturePage.isLinked,
            tooltip: {
              text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
              textAlign: TextAlign.LEFT,
              enabled: !!lecturePage.isLinked,
              placement: 'left',
            },
          },
          {
            type: 'divider',
          },
          {
            type: 'radio',
            label: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.SINGLE_SELECTION(),
            id: 'poll-question-type-single-selection',
            group: 'poll-question-type',
            itemStyle: editDropdownRadioButtonStyle,
            infoIconTooltipText: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.SINGLE_SELECTION_INFO_TOOLTIP(
              { learnerAlias: course.learnersName.capitalizedPluralized },
            ),
            isChecked: pollQuestion.type === PollQuestionType.MULTIPLE_CHOICE,
            dataQa: config.pendo.polls.selectSingleSelectionPoll,
            callback: () => {
              dispatch(updatePollQuestion({
                catalogId,
                questionId: pollQuestion.id,
                questionParams: {
                  type: PollQuestionType.MULTIPLE_CHOICE,
                },
              }));
            },
            disabled: !canMakeStructuralChanges,
            tooltip: {
              text: lecturePage.isLinked
                ? t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP()
                : t.LECTURE_PAGES.COMPONENTS.POLLS.POLL_RELEASED(
                  { learnersAlias: course.learnersName.downcasedPluralized },
                ),
              textAlign: TextAlign.LEFT,
              enabled: !canMakeStructuralChanges,
              placement: 'left',
            },
          },
          {
            type: 'radio',
            label: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.MULTIPLE_SELECTION(),
            id: 'poll-question-type-multiple-selection',
            group: 'poll-question-type',
            itemStyle: editDropdownRadioButtonStyle,
            infoIconTooltipText: t.LECTURE_PAGES.COMPONENTS.POLLS.EDIT_DROPDOWN.MULTIPLE_SELECTION_INFO_TOOLTIP(
              { learnerAlias: course.learnersName.capitalizedPluralized },
            ),
            isChecked: pollQuestion.type === PollQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER,
            dataQa: config.pendo.polls.selectMultipleSelectionPoll,
            callback: () => {
              dispatch(updatePollQuestion({
                catalogId,
                questionId: pollQuestion.id,
                questionParams: {
                  type: PollQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER,
                },
              }));
            },
            disabled: !canMakeStructuralChanges,
            tooltip: {
              text: lecturePage.isLinked
                ? t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP()
                : t.LECTURE_PAGES.COMPONENTS.POLLS.POLL_RELEASED(
                  { learnersAlias: course.learnersName.downcasedPluralized },
                ),
              textAlign: TextAlign.LEFT,
              enabled: !canMakeStructuralChanges,
              placement: 'left',
            },
          },
        ],
      },
    });
  }, [
    course.isSelfPaced,
    lectureComponent.poll.expirationDate,
    lectureComponent.poll.responsePrivacySetting,
    lecturePage.releaseDate,
    setSharedProps,
    updater,
    pollQuestion.id,
    pollQuestion.type,
    catalogId,
    dispatch,
    course.isContentManagementCollection,
    lecturePage.isLinked,
  ]);
}

const editDropdownRadioButtonStyle = css`
  label {
    font-size: 14px;
  }
`;
type SelectedResponseVotesCountType = { [key: number]: number };

export type NewVotePushNotificationPayload = {
  poll_id: number;
  selected_response_option: number[];
  user_id: number;
  /**
   *  note that this value represents the count when the notification is sent
   *  and not when the vote is emitted
   */
  selected_response_votes_count: SelectedResponseVotesCountType;
  question_set_submission_id: number;
};

export type NewVotePushNotificationPayloadCamelCased = {
  pollId: number;
  selectedResponseOption: number[];
  userId: number;
  selectedResponseVotesCount: SelectedResponseVotesCountType;
  questionSetSubmissionId: number;
};

/* @ngInject */
export function useVotesPushNotificationSubscriber() {
  const dispatch = useAppDispatch();
  const lectureComponent = useGetPollLectureComponent();
  const { isEdit, catalogId } = useLectureComponentContext();

  // selectors
  const question = useSelector((state) => getPollQuestion(state, { pollQuestionId: lectureComponent.poll.questionIds[0] }));
  const pollVieweeSubmission = useSelector((state) => getNormalizedPollVieweeSubmission(state, { vieweeSubmissionId: lectureComponent.poll.vieweeSubmissionId }));

  // used to store the most up-to-date vote count by option id. This is needed because
  // notifications may arrive out of order
  const newestVoteCountArrivedByOptionId = React.useRef<{ [key: number]: number }>({});
  // keeping the count of votes being processed (e.g. fetching the user information) before being actually counted
  const votesBeingProcessedByOptionId = React.useRef<{ [key: number]: number }>({});

  const canReceiveVotes = !isEdit && (lectureComponent.poll.progress !== 'missed' || !lectureComponent.poll.hardDeadline);

  // create these ref to store the values and keeping them up-to-date to be used
  // in the pusher event listener and without causing the dependency list to invalidate
  const pollVieweeSubmissionRef = React.useRef<PollVieweeSubmissionNormalized>();
  const pollOptionRef = React.useRef<PollOptionNormalized[]>();
  React.useEffect(() => {
    pollVieweeSubmissionRef.current = pollVieweeSubmission;
    pollOptionRef.current = question.responseOptions;
  });

  React.useEffect(() => {
    const channel = `public-poll-${lectureComponent.poll.id}`;
    if (canReceiveVotes) {
      const pusherChannel = PusherService.setupChannel(channel);
      pusherChannel.bind('new_vote', (voteNotificationPayload: NewVotePushNotificationPayload) => {
        const pusherData = humps.camelizeKeys(voteNotificationPayload) as unknown as NewVotePushNotificationPayloadCamelCased;

        const {
          questionSetSubmissionId,
          selectedResponseOption,
          selectedResponseVotesCount,
          userId,
        } = pusherData;

        // if is not the current user's vote
        if (questionSetSubmissionId !== pollVieweeSubmissionRef.current?.id) {
          // get the option data. May be undefined in case that the current
          // option has no votes

          selectedResponseOption.forEach((eachSelectedResponseOption) => {
            const currentOption = pollOptionRef.current.find(
              (option) => option.id === eachSelectedResponseOption,
            );

            votesBeingProcessedByOptionId.current[eachSelectedResponseOption] = votesBeingProcessedByOptionId.current[eachSelectedResponseOption] ?? 0;
            newestVoteCountArrivedByOptionId.current[eachSelectedResponseOption] = Math.max(
              selectedResponseVotesCount[eachSelectedResponseOption],
              newestVoteCountArrivedByOptionId.current[selectedResponseVotesCount[eachSelectedResponseOption]] ?? 0,
            );

            const isAvatarAlreadyIncluded = currentOption.voterIds?.some(
              (voterId) => voterId === userId,
            );

            const processedVotesCount = currentOption?.votesCount + votesBeingProcessedByOptionId.current[eachSelectedResponseOption];
            if (
              !isAvatarAlreadyIncluded
            // count te vote only if we are expecting to receive more votes
            && processedVotesCount < newestVoteCountArrivedByOptionId.current[eachSelectedResponseOption]
            ) {
              votesBeingProcessedByOptionId.current[eachSelectedResponseOption] += 1;
              dispatch(addNewVote({ ...pusherData, optionId: eachSelectedResponseOption, catalogId })).then(() => {
                votesBeingProcessedByOptionId.current[eachSelectedResponseOption] -= 1;
              });
            }
          });
        }
      });
    }

    return () => {
      if (canReceiveVotes) {
        PusherService.removeChannel(channel);
      }
    };
  }, [canReceiveVotes, catalogId, dispatch, lectureComponent.poll.id]);
}
